<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-layout>
        <v-spacer></v-spacer>
        <v-btn class="mx-1 primary" @click="() => showGenerateReportForm(report)">Download Report</v-btn>
      </v-layout>
    </v-flex>
    <v-flex xs12>
      <v-card flat class="elevation-1">
        <v-card-text class="pa-0">
          <v-btn flat @click="back">
            <v-icon left dark>keyboard_arrow_left</v-icon>
            <span v-if="report">{{ report.name }}</span>
          </v-btn>
        </v-card-text>
      </v-card>
      <v-data-table
        class="elevation-1"
        :loading="reportLoading"
        :headers="headers"
        :items="report ? report.clips : []"
        :rows-per-page-items="rowsPerPageItems"
      >
        <template v-slot:items="props">
          <td class="text-xs-center">{{ props.item.journal.publishDate | parseFormat3 }}</td>
          <td>{{ props.item.media.name }}</td>
          <td>{{ props.item.headline | limitWords }}</td>
          <td>{{ props.item.tone }}</td>
          <td>{{ props.item.prValue | txtInAmt }}</td>
          <td class="text-xs-center">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small @click="() => editClip(props.item)">edit</v-icon>
              </template>
              <span>Edit Clip</span>
            </v-tooltip>
            &nbsp;
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small @click="() => removeClip(props.item.uuid)">delete</v-icon>
              </template>
              <span>Delete Clip</span>
            </v-tooltip>
            &nbsp;
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small @click="() => showArticleView(props.item)">remove_red_eye</v-icon>
              </template>
              <span>Article View</span>
            </v-tooltip>
          </td>
        </template>
      </v-data-table>
    </v-flex>

    <v-dialog v-model="downloadReportLoading" width="200" persistent>
      <v-card>
        <v-card-text class="text-xs-center">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="clipFormDialog" :width="800" persistent>
      <ClipForm
        :croppedImage="croppedImage"
        :page="page"
        :reports="reports"
        :editClip="clip"
        :download="true"
        :minimizable="false"
        @save="saveClip"
        @cancel="cancelClip"
      />
    </v-dialog>

    <v-dialog v-model="articleViewDialog" :width="700" persistent>
      <ClipArticleView :clip="viewClip" @closeview="articleViewDialog = false" />
    </v-dialog>
    
    <v-dialog v-model="generateReportFormDialog" :width="570" persistent>
      <GenerateReportForm 
        :report="report"
        :show="generateReportFormDialog"
        @cancel="generateReportFormDialog = false" 
        @generate="payload => processReport({ reportID: currentReportID, payload })"
      />
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import ClipForm from "@/components/ClipForm";
import ClipArticleView from "@/components/ClipArticleView";
import custom_date from "@/mixins/custom_date";
import custom_text from "@/mixins/custom_text";
import reportdownload from "@/mixins/reportdownload";
import GenerateReportForm from "@/components/GenerateReportForm";

export default {
  mixins: [ custom_date, reportdownload, custom_text ],

  components: {
    ClipForm,
    ClipArticleView,
    GenerateReportForm,
  },

  data() {
    return {
      headers: [
        {
          text: "Publish Date",
          value: "publishDate",
          sortable: false,
          align: "center",
        },
        {
          text: "Media",
          value: "media",
          sortable: false,
        },
        {
          text: "Headline",
          value: "headline",
          sortable: false,
        },
        {
          text: "Tone",
          value: "tone",
          sortable: false,
        },
        {
          text: "PR Value",
          value: "prValue",
          sortable: false,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
        }
      ],

      rowsPerPageItems: [ 50 ],

      clipFormDialog: false,

      articleViewDialog: false,

      viewClip: undefined,

      croppedImage: {
        url: undefined,
      },

      page: {
        media: "",
        publishDate: "",
      },

      clip: {
        reportID: "",
        headline: "",
        summary: "",
      },

      clipID: undefined,

      generateReportFormDialog: false,
    };
  },

  created() {
    this.fetchReport(this.currentReportID);
    this.fetchReports();
  },

  computed: {
    currentReportID() {
      return this.$route.params.id;
    },

    ...mapState("clips", [
      "report",
      "reportLoading",
    ]),

    ...mapState("reports", [
      "reports",
    ]),
  },

  methods: {
    ...mapActions("clips", [
      "fetchReport",
      "deleteClip",
      "updateClip",
    ]),

    ...mapActions("reports", [
      "fetchReports",
    ]),

    ...mapMutations("clips", [
      "setReportLoading",
      "resetState",
    ]),

    editClip(clip) {
      this.clipID = clip.uuid;

      this.page = Object.assign({}, {
        media: clip.media.name, 
        publishDate: clip.journal.publishDate,
        pageNo: clip.page.pageNo,
        keywords: clip.keywords,
      });

      this.croppedImage.url = clip.screenshot;

      this.clip = Object.assign({}, {
        reportID: this.report.uuid,
        headline: clip.headline,
        summary: clip.summary,
        tone: clip.tone,
        prValue: clip.prValue,
        author: clip.author,
      });

      this.clipFormDialog = true;
    },

    removeClip(clipID) {
      if (confirm("Are you sure to delete this clip?")) {
        this.deleteClip(clipID);
        this.$ua.trackEvent('DeleteClip', 'click',
          `${this.currentUser.username || 'User'} deleted ${clipID}`);
      }
    },

    cancelClip() {
      this.clipFormDialog = false;

      this.clip = Object.assign({}, {
        reportID: "",
        headline: "",
        summary: "",
      });
    },

    saveClip(clip) {
      const payload = Object.assign({}, clip);
      this.updateClip({ clipID: this.clipID, payload })
          .then(() => {
            this.clip = Object.assign({}, {
              reportID: "",
              headline: "",
              summary: "",
            });
            this.clipFormDialog = false;
          });
      this.$ua.trackEvent('UpdateClip', 'click',
        `${this.currentUser.username || 'User'} updated ${this.clipID} with payload ${payload}`);
    },

    back() {
      this.$router.push({ name: "reports" });
      this.resetState();
    },

    goClipping(clip) {
      const param = { 
        name: "clipping", 
        params: { id: clip.page.id }, 
        query: { keywords: clip.keywords } 
      };
      const routeData = this.$router.resolve(param);
      window.open(routeData.href, '_blank');
    },

    showArticleView(clip) {
      this.articleViewDialog = true;
      this.viewClip = clip;
    },

    showGenerateReportForm() {
      this.generateReportFormDialog = true;
    },
  },

  filters: {
    limitWords(val) {
      return val.length > 50 ? val.substring(0, 50) + "..." : val;
    }
  }
}
</script>

<style scoped>

</style>